import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, Wind, Gauge, ShieldCheck } from "lucide-react";
import RevealLine from "@/components/RevealLine";
import Reveal from "@/components/Reveal";
import ClipImage from "@/components/ClipImage";
import Marquee from "@/components/Marquee";
import { CompareTable } from "@/components/CompareTable";
import { api } from "@/lib/api";
import { useLang } from "@/i18n";

const HERO_BG =
  "https://images.unsplash.com/photo-1695970922375-34520eca9bed?crop=entropy&cs=srgb&fm=jpg&q=90";
const CARBON_IMG =
  "https://images.unsplash.com/photo-1758599543129-5269a8f29e68?crop=entropy&cs=srgb&fm=jpg&q=85";
const PIPES_IMG =
  "https://images.unsplash.com/photo-1708705261211-b2a244a811dc?crop=entropy&cs=srgb&fm=jpg&q=90";

const CHAPTERS = [
  {
    n: "01",
    kicker: { it: "PRINCIPIO", en: "PRINCIPLE" },
    title: { it: "L'aria non è un dettaglio", en: "Air is not a detail" },
    body: {
      it: "È l'infrastruttura invisibile che decide produttività, salute e reputazione. Noi la trattiamo come tale: misurabile, certificata, silenziosa.",
      en: "It is the invisible infrastructure that determines productivity, health and reputation. We treat it as such: measurable, certified, silent.",
    },
  },
  {
    n: "02",
    kicker: { it: "METODO", en: "METHOD" },
    title: { it: "Ingegneria multi-stadio", en: "Multi-stage engineering" },
    body: {
      it: "HEPA H14, carboni attivi impregnati, UV-C ad alta intensità, sensori IoT calibrati. Ogni stadio è un dato, ogni dato è una decisione.",
      en: "HEPA H14, impregnated activated carbon, high-intensity UV-C, calibrated IoT sensors. Every stage is a data point, every data point a decision.",
    },
  },
  {
    n: "03",
    kicker: { it: "IMPATTO", en: "IMPACT" },
    title: { it: "Impianti carbon-neutral", en: "Carbon-neutral systems" },
    body: {
      it: "Con Carbonplai portiamo ogni installazione verso la neutralità climatica verificata. Report trimestrali. Compensazione Gold Standard.",
      en: "With Carbonplai we bring every installation towards verified climate neutrality. Quarterly reports. Gold Standard offsetting.",
    },
  },
];

const KPI = [
  { v: "99,97%", l: { it: "abbattimento di particelle e allergeni", en: "removal of particles and allergens" } },
  { v: "2500", l: { it: "m³/h CADR massimo (PRO MAX)", en: "m³/h max CADR (PRO MAX)" } },
  { v: "19dB", l: { it: "rumorosità in modalità Sleep", en: "noise level in Sleep mode" } },
  { v: "24/7", l: { it: "monitoraggio via App iOS/Android", en: "monitoring via iOS/Android App" } },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const { lang, tr } = useLang();
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 800], [0, 200]);
  const heroOpacity = useTransform(scrollY, [0, 500], [1, 0.15]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="page-home" className="bg-white text-[#0a0a0a]">
      {/* ─── HERO ─────────────────────────────────────────────── */}
      <section className="relative min-h-screen w-full overflow-hidden pt-24">
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="absolute inset-0 z-0" aria-hidden>
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/40 to-white" />
        </motion.div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 lg:px-12 pt-16">
          <div className="flex items-center justify-end font-mono text-[10px] tracking-[0.25em] text-[#3f3f46] uppercase">
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4, duration: 0.8 }}
              className="hidden md:inline text-[#007A8A]"
            >
              {tr({ it: "PARTNER STRATEGICO · CARBONPLAI", en: "STRATEGIC PARTNER · CARBONPLAI" })}
            </motion.span>
          </div>
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 lg:px-12 pt-24 pb-24 md:pt-40 md:pb-32">
          <h1
            data-testid="hero-title"
            className="font-display text-[clamp(3.2rem,11vw,11.5rem)] leading-[0.86] tracking-[-0.04em]"
          >
            {lang === "en" ? (
              <>
                <div><RevealLine delay={0.4}>We engineer</RevealLine></div>
                <div>
                  <RevealLine delay={0.55}>what we</RevealLine>{" "}
                  <RevealLine delay={0.7} className="font-editorial italic font-normal text-[#007A8A]">
                    breathe.
                  </RevealLine>
                </div>
              </>
            ) : (
              <>
                <div><RevealLine delay={0.4}>Progettiamo</RevealLine></div>
                <div>
                  <RevealLine delay={0.55}>quello che</RevealLine>{" "}
                  <RevealLine delay={0.7} className="font-editorial italic font-normal text-[#007A8A]">
                    respiriamo.
                  </RevealLine>
                </div>
              </>
            )}
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-16 items-end">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 1 }}
              className="lg:col-span-6 font-body text-lg text-[#3f3f46] leading-relaxed max-w-xl"
            >
              {tr({
                it: "Aéràs Technology progetta sistemi di purificazione, monitoraggio e trattamento dell'aria per abitazioni, uffici, sanità e ambienti ad alta performance. Filtrazione HEPA, sensoristica intelligente, integrazione carbon-neutral con Carbonplai.",
                en: "Aéràs Technology engineers air purification, monitoring and treatment systems for homes, offices, healthcare and high-performance environments. HEPA filtration, smart sensors, carbon-neutral integration with Carbonplai.",
              })}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 1 }}
              className="lg:col-span-6 flex flex-wrap items-center gap-4 lg:justify-end"
            >
              <Link to="/preventivo" data-testid="hero-cta-preventivo" className="btn-primary">
                {tr({ it: "Richiedi preventivo", en: "Request a quote" })} <ArrowRight size={14} />
              </Link>
              <Link to="/prova-a-casa" data-testid="hero-cta-trial" className="btn-ghost">
                {tr({ it: "Prova a casa", en: "Home trial" })}
              </Link>
            </motion.div>
          </div>
        </div>

        {/* KPI strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="relative z-10 border-t border-[#e4e4e7] bg-white/60 backdrop-blur"
        >
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-4">
            {KPI.map((k, i) => (
              <div
                key={i}
                className={`py-8 md:py-10 ${i !== 0 ? "md:border-l border-[#e4e4e7]" : ""} ${
                  i === 1 ? "border-l" : ""
                } ${i > 1 ? "border-t md:border-t-0" : ""} border-[#e4e4e7] px-6`}
              >
                <div className="font-display text-3xl md:text-5xl tracking-tighter">{k.v}</div>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#52525b] mt-2">
                  {tr(k.l)}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ─── MANIFESTO ─────────────────────── */}
      <section className="relative py-32 md:py-40 border-t border-[#e4e4e7]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-6">— MANIFESTO</div>
            <h2 className="font-display text-[clamp(2.5rem,6vw,5.5rem)] leading-[0.9] tracking-tighter max-w-5xl">
              {tr({
                it: "Tre principi che definiscono ogni impianto Aéràs.",
                en: "Three principles that define every Aéràs system.",
              })}
            </h2>
          </Reveal>

          <div className="mt-24 space-y-24 md:space-y-32">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.05}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 border-t border-[#e4e4e7] pt-12">
                  <div className="md:col-span-4">
                    <div className="font-editorial italic text-[clamp(5rem,12vw,10rem)] leading-[0.8] text-[#18181b]">
                      {c.n}
                    </div>
                    <div className="font-mono text-[10px] tracking-[0.3em] text-[#3f3f46] mt-2">{tr(c.kicker)}</div>
                  </div>
                  <div className="md:col-span-7 md:col-start-6">
                    <h3 className="font-display text-3xl md:text-5xl tracking-tighter leading-[1] mb-6">
                      {tr(c.title)}
                    </h3>
                    <p className="font-body text-lg text-[#3f3f46] leading-relaxed max-w-2xl">{tr(c.body)}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── MARQUEE ─────────────────────────────────────────── */}
      <Marquee
        words={
          lang === "en"
            ? ["Breathe innovation", "Advanced purification", "Air as infrastructure", "Carbon-neutral by design"]
            : ["Respira l'innovazione", "Purificazione avanzata", "Aria come infrastruttura", "Carbon-neutral by design"]
        }
      />

      {/* ─── PRODUCTS ─────────────────────── */}
      <section className="py-32 md:py-40 border-t border-[#e4e4e7]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between flex-wrap gap-8 mb-16">
            <Reveal>
              <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-6">
                {tr({ it: "— PRODOTTI", en: "— PRODUCTS" })}
              </div>
              <h2 className="font-display text-[clamp(2.5rem,6vw,5.5rem)] leading-[0.9] tracking-tighter">
                {tr({ it: "Tre sistemi.", en: "Three systems." })}<br />
                <span className="font-editorial italic font-normal text-[#3f3f46]">
                  {tr({ it: "Un solo standard.", en: "One standard." })}
                </span>
              </h2>
            </Reveal>
            <Link to="/prodotti" data-testid="home-products-cta" className="btn-ghost">
              {tr({ it: "Tutti i prodotti", en: "All products" })} <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {products.slice(0, 3).map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08}>
                <Link
                  to="/prodotti"
                  data-testid={`home-product-${p.name.toLowerCase().replace(/\s/g, "-")}`}
                  className="group block border border-[#e4e4e7] hover:border-[#007A8A] transition-colors bg-[#f4f4f5] h-full"
                >
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <div className="absolute inset-0 spotlight opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-10" />
                    <img
                      src={p.image}
                      alt={p.name}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-1000"
                    />
                    <div className="absolute top-4 left-4 z-20 font-mono text-[10px] tracking-[0.25em] text-white/80 bg-black/60 px-2 py-1">
                      0{i + 1} / {String(Math.min(3, products.length)).padStart(2, "0")}
                    </div>
                  </div>
                  <div className="p-6 lg:p-8 border-t border-[#e4e4e7]">
                    <div className="font-mono text-[10px] tracking-[0.25em] text-[#3f3f46] mb-3">
                      {(lang === "en" ? p.category_en || p.category : p.category)?.toUpperCase()}
                    </div>
                    <div className="font-display text-3xl tracking-tighter mb-2">{p.name}</div>
                    <div className="font-body text-sm text-[#3f3f46]">
                      {lang === "en" ? p.tagline_en || p.tagline : p.tagline}
                    </div>
                    <div className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-[#007A8A]">
                      {tr({ it: "SCHEDA TECNICA", en: "DATA SHEET" })} <ArrowUpRight size={12} />
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── COMPARISON TABLE ─────────────────────── */}
      <section className="py-32 md:py-40 border-t border-[#e4e4e7]" data-testid="home-compare-section">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-6">
              {tr({ it: "— CONFRONTA I MODELLI", en: "— COMPARE MODELS" })}
            </div>
            <h2 className="font-display text-[clamp(2.5rem,6vw,5.5rem)] leading-[0.9] tracking-tighter mb-16 max-w-4xl">
              {tr({ it: "Quale AIR FORCE ONE", en: "Which AIR FORCE ONE" })}{" "}
              <span className="font-editorial italic font-normal text-[#3f3f46]">
                {tr({ it: "fa per te?", en: "is right for you?" })}
              </span>
            </h2>
          </Reveal>
          <CompareTable />
        </div>
      </section>

      {/* ─── CARBONPLAI PARTNER ─────────────────────── */}
      <section className="py-32 md:py-40 border-t border-[#e4e4e7] relative">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <Reveal className="lg:col-span-7">
              <div className="border border-[#e4e4e7] p-8 md:p-12 lg:p-16 h-full bg-[#f4f4f5] relative overflow-hidden">
                <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-8">
                  {tr({ it: "● PARTNER STRATEGICO", en: "● STRATEGIC PARTNER" })}
                </div>
                <h3 className="font-display text-[clamp(2.5rem,5vw,4.5rem)] leading-[0.95] tracking-tighter mb-8">
                  <span className="text-[#3f3f46]">Aéràs</span> × <span className="text-[#0a0a0a]">Carbonplai</span>
                </h3>
                <p className="font-body text-lg text-[#3f3f46] leading-relaxed max-w-xl mb-10">
                  {tr({
                    it: "Ogni impianto Aéràs è integrato con la piattaforma Carbonplai per la misurazione, il reporting e la compensazione dell'impatto climatico. Un ecosistema che rende l'aria pulita anche carbon-negative.",
                    en: "Every Aéràs system integrates with the Carbonplai platform for measuring, reporting and offsetting climate impact. An ecosystem that makes clean air carbon-negative too.",
                  })}
                </p>
                <a
                  href="https://carbonplai.com"
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="home-carbonplai-link"
                  className="btn-ghost"
                >
                  carbonplai.com <ArrowUpRight size={14} />
                </a>

                <div className="hidden md:block absolute right-6 top-8 bottom-8 font-mono text-[10px] tracking-[0.4em] text-[#a1a1aa] [writing-mode:vertical-rl] rotate-180 uppercase">
                  Certified · Carbon-Neutral · Aéràs × Carbonplai · 2025
                </div>
              </div>
            </Reveal>

            <div className="lg:col-span-5 flex flex-col gap-6">
              <ClipImage src={CARBON_IMG} alt="Partnership Carbonplai" className="aspect-[4/3] border border-[#e4e4e7]" />
              <div className="grid grid-cols-2 gap-6 flex-1">
                <div className="border border-[#e4e4e7] p-6 bg-[#f4f4f5]">
                  <ShieldCheck size={20} className="text-[#007A8A]" />
                  <div className="font-display text-2xl mt-4 leading-tight tracking-tight">Gold Standard</div>
                  <div className="font-mono text-[10px] tracking-widest text-[#52525b] mt-2">CERTIFIED OFFSET</div>
                </div>
                <div className="border border-[#e4e4e7] p-6 bg-[#f4f4f5]">
                  <Gauge size={20} className="text-[#007A8A]" />
                  <div className="font-display text-2xl mt-4 leading-tight tracking-tight">Report Q1—Q4</div>
                  <div className="font-mono text-[10px] tracking-widest text-[#52525b] mt-2">LIVE DASHBOARD</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── IMAGE PARALLAX FEATURE ────────────────────────── */}
      <section className="relative border-t border-[#e4e4e7] overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
          <div className="lg:col-span-6 relative aspect-square lg:aspect-auto lg:min-h-[700px] overflow-hidden">
            <ClipImage src={PIPES_IMG} alt="Impianti HVAC industriali" className="absolute inset-0" />
          </div>
          <div className="lg:col-span-6 p-8 md:p-16 lg:p-24 flex flex-col justify-center border-l border-[#e4e4e7]">
            <Reveal>
              <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-6">
                {tr({ it: "— INFRASTRUTTURA", en: "— INFRASTRUCTURE" })}
              </div>
              <h3 className="font-display text-[clamp(2rem,4vw,3.5rem)] leading-[0.95] tracking-tighter mb-8">
                {tr({ it: "Dal sensore al report ESG,", en: "From sensor to ESG report," })}{" "}
                <span className="font-editorial italic font-normal text-[#3f3f46]">
                  {tr({ it: "un solo flusso.", en: "one single flow." })}
                </span>
              </h3>
              <p className="font-body text-[#3f3f46] text-lg leading-relaxed mb-10 max-w-lg">
                {tr({
                  it: "Sensori intelligenti in ogni purificatore, App iOS/Android e dati in tempo reale. Progettiamo il livello fisico e il livello dato con la stessa cura.",
                  en: "Smart sensors in every purifier, iOS/Android App and real-time data. We engineer the physical layer and the data layer with equal care.",
                })}
              </p>
              <div className="grid grid-cols-3 gap-6 max-w-md">
                <div>
                  <Wind size={18} className="text-[#007A8A] mb-3" />
                  <div className="font-display text-2xl tracking-tighter">7</div>
                  <div className="font-mono text-[10px] tracking-widest text-[#52525b] mt-1">
                    {tr({ it: "STADI FILTRO", en: "FILTER STAGES" })}
                  </div>
                </div>
                <div>
                  <Gauge size={18} className="text-[#007A8A] mb-3" />
                  <div className="font-display text-2xl tracking-tighter">1s</div>
                  <div className="font-mono text-[10px] tracking-widest text-[#52525b] mt-1">
                    {tr({ it: "LATENZA", en: "LATENCY" })}
                  </div>
                </div>
                <div>
                  <ShieldCheck size={18} className="text-[#007A8A] mb-3" />
                  <div className="font-display text-2xl tracking-tighter">2 {tr({ it: "anni", en: "yrs" })}</div>
                  <div className="font-mono text-[10px] tracking-widest text-[#52525b] mt-1">
                    {tr({ it: "GARANZIA", en: "WARRANTY" })}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ─── CTA CLOSER ─────────────────────────────────────── */}
      <section className="py-32 md:py-48 border-t border-[#e4e4e7] relative overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-8">
            <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-6">
              {tr({ it: "— PROSSIMO PASSO", en: "— NEXT STEP" })}
            </div>
            <h2 className="font-display text-[clamp(2.5rem,7vw,7rem)] leading-[0.9] tracking-tighter">
              {lang === "en" ? (
                <>
                  Let's design <br />
                  <span className="font-editorial italic font-normal">together</span> your system.
                </>
              ) : (
                <>
                  Progettiamo <br />
                  <span className="font-editorial italic font-normal">insieme</span> il tuo impianto.
                </>
              )}
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-4 flex flex-col gap-4">
            <p className="font-body text-[#3f3f46] text-lg leading-relaxed">
              {tr({
                it: "Sopralluogo tecnico gratuito, analisi ambientale, proposta con ROI e prova a casa di un giorno con un nostro tecnico. Rispondiamo entro 24 ore.",
                en: "Free technical survey, environmental analysis, ROI proposal and a one-day home trial with our technician. We respond within 24 hours.",
              })}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/preventivo" data-testid="home-final-cta" className="btn-primary w-fit">
                {tr({ it: "Richiedi preventivo", en: "Request a quote" })} <ArrowRight size={14} />
              </Link>
              <Link to="/prova-a-casa" data-testid="home-final-trial-cta" className="btn-ghost w-fit">
                {tr({ it: "Prova a casa", en: "Home trial" })}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
